/**
 * Meta Pixel (Facebook Pixel) integration for BodAI
 * Provides Facebook tracking functionality for ads optimization
 */

/**
 * Initialize Meta Pixel
 * This should be called once when the app loads
 */
export const initMetaPixel = () => {
  // Get pixel ID from environment variables
  const pixelId = import.meta.env.VITE_META_PIXEL_ID;

  // Only initialize if pixel ID is provided
  if (!pixelId) {
    console.warn('[Meta Pixel] Meta Pixel ID not found. Meta Pixel disabled.');
    console.warn('[Meta Pixel] Checked: VITE_META_PIXEL_ID');
    return null;
  }

  console.log('[Meta Pixel] Initializing with pixel ID:', pixelId);

  // Load Facebook Pixel script
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');

  // Initialize pixel
  window.fbq('init', pixelId);

  // Track page view
  window.fbq('track', 'PageView');

  console.log('[Meta Pixel] Initialized successfully');

  return window.fbq;
};

/**
 * Get Meta Pixel instance
 * Returns null if Meta Pixel is not initialized
 */
export const getMetaPixel = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    return window.fbq;
  }
  return null;
};

/**
 * Track custom events
 * Wrapper around fbq with error handling
 */
export const trackEvent = (eventName, parameters = {}) => {
  const fbq = getMetaPixel();
  if (fbq) {
    try {
      fbq('track', eventName, parameters);
      if (import.meta.env.DEV) {
        console.log('[Meta Pixel] Event tracked:', eventName, parameters);
      }
    } catch (error) {
      console.error('[Meta Pixel] Error tracking event:', error);
    }
  }
};

/**
 * Track custom events with custom parameters
 * Wrapper around fbq with error handling
 */
export const trackCustom = (eventName, parameters = {}) => {
  const fbq = getMetaPixel();
  if (fbq) {
    try {
      fbq('trackCustom', eventName, parameters);
      if (import.meta.env.DEV) {
        console.log('[Meta Pixel] Custom event tracked:', eventName, parameters);
      }
    } catch (error) {
      console.error('[Meta Pixel] Error tracking custom event:', error);
    }
  }
};

/**
 * Track standard e-commerce events
 */
export const trackPurchase = (value, currency = 'USD', parameters = {}) => {
  trackEvent('Purchase', {
    value: value,
    currency: currency,
    ...parameters
  });
};

export const trackInitiateCheckout = (value, currency = 'USD', parameters = {}) => {
  trackEvent('InitiateCheckout', {
    value: value,
    currency: currency,
    ...parameters
  });
};

export const trackAddToCart = (value, currency = 'USD', parameters = {}) => {
  trackEvent('AddToCart', {
    value: value,
    currency: currency,
    ...parameters
  });
};

export const trackViewContent = (contentType, contentId, parameters = {}) => {
  trackEvent('ViewContent', {
    content_type: contentType,
    content_id: contentId,
    ...parameters
  });
};

export const trackLead = (parameters = {}) => {
  trackEvent('Lead', parameters);
};

export const trackCompleteRegistration = (parameters = {}) => {
  trackEvent('CompleteRegistration', parameters);
};

/**
 * Track user engagement events specific to BodAI
 */
export const trackVideoAnalysis = (videoDuration, analysisType) => {
  trackCustom('VideoAnalysis', {
    video_duration: videoDuration,
    analysis_type: analysisType,
    content_type: 'video'
  });
};

export const trackPracticeSession = (sessionType, duration) => {
  trackCustom('PracticeSession', {
    session_type: sessionType,
    duration: duration,
    content_type: 'practice'
  });
};

export const trackSubscription = (planType, value, currency = 'USD') => {
  trackPurchase(value, currency, {
    content_type: 'subscription',
    plan_type: planType
  });
};

// Export default fbq instance for direct access if needed
export default window.fbq;



